import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GameGrid } from "@/components/GameGrid";
import { QUOTES } from "@/data/quotes";
import { GAMES } from "@/data/games";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/games/")({
  head: () => ({
    meta: [
      { title: "Mr. B Quotes Games — Daily Word Puzzles & Trivia" },
      { name: "description", content: "Challenge yourself daily with Mr. B Quotes Games! Play Quote Wordle, Scramble, Who Said This?, True or False, and more. Learn, level up, & stream live." },
      { property: "og:title", content: "Mr. B Quotes Games" },
      { property: "og:description", content: "Free addictive quote games and word puzzles for fans, streams, and trivia nights." },
      { property: "og:url", content: `${SITE}/games` },
    ],
    links: [{ rel: "canonical", href: `${SITE}/games` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Mr. B Quotes Games",
          description: "A collection of free quote-based trivia and puzzle games.",
          url: `${SITE}/games`,
          hasPart: GAMES.map((g) => ({
            "@type": "VideoGame",
            name: g.name,
            url: `${SITE}/games/${g.slug}`,
            genre: g.type === "trivia" ? "Trivia" : "Puzzle",
          })),
        }),
      },
    ],
  }),
  component: GamesHome,
});

function GamesHome() {
  const featured = QUOTES[Math.floor(Date.now() / (1000 * 60 * 60 * 24)) % QUOTES.length];
  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Games" active="games" />
      <main id="main">

      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-4 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-center">
            <div className="animate-reveal">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/50 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {GAMES.length} games · Built for the stream
              </div>
              <h1 className="mt-6 font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
                The world's <span className="text-gold italic">finest quotes,</span> now playable.
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                {GAMES.length} interactive games from <strong className="text-foreground">Mr. B Quotes</strong> — guess authors, finish lines, unscramble wisdom, spot the fakes, and crack the daily Quote Wordle. Curated from {QUOTES.length}+ sourced quotations.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#games" className="rounded-full bg-gradient-gold px-6 py-3 text-sm font-semibold text-ink shadow-gold hover:opacity-90 transition">Choose a game</a>
                <Link to="/live" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-gold transition">Open Livestream Mode</Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                <Stat n={`${QUOTES.length}+`} l="Curated quotes" />
                <Stat n={String(GAMES.length)} l="Game modes" />
                <Stat n="∞" l="Replay value" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-gold opacity-10 blur-3xl rounded-full" />
              <article className="relative rounded-2xl border border-border bg-card/80 p-8 sm:p-10 shadow-elegant grain">
                <div className="quote-mark" />
                <p className="font-display text-2xl sm:text-3xl leading-tight text-foreground">{featured.text}</p>
                <div className="mt-6 flex items-center justify-between border-t border-border pt-5">
                  <div>
                    <div className="text-sm text-gold font-medium">— {featured.author}</div>
                    {featured.era && <div className="text-xs text-muted-foreground mt-0.5">{featured.era}{featured.source ? ` · ${featured.source}` : ""}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Quote of the day</span>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section id="games" className="border-t border-border/60 bg-ink/40">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <div className="mb-10 flex items-end justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">The Collection</div>
              <h2 className="mt-2 font-display text-4xl sm:text-5xl">{GAMES.length} ways to play</h2>
            </div>
            <p className="hidden sm:block max-w-sm text-sm text-muted-foreground">Every game opens onto its own hub with packs, levels, daily challenges and stream tools.</p>
          </div>
          <GameGrid />
        </div>
      </section>

      <section className="border-t border-border/60">
        <div className="mx-auto max-w-6xl px-4 py-16 grid gap-10 md:grid-cols-3">
          <Feature title="Curated quote library" body={`${QUOTES.length} widely-attributed quotations from Socrates and Marcus Aurelius to Maya Angelou and Steve Jobs — every entry tied to a real, citable source.`} />
          <Feature title="Packs, levels & streaks" body="Filter trivia by pack — Stoic, Civil Rights, Scientists. Climb five Scramble levels. Build streaks for multipliers in every trivia mode." />
          <Feature title="Made for streamers" body="Live Mode adds oversized type, an on-screen timer, and A/B/C/D answer codes so chat can vote in real time. No accounts, ever." />
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div>
      <div className="font-display text-3xl text-gold">{n}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{l}</div>
    </div>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="font-display text-xl text-gold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}
